/**
 * Génération de codes-barres Code 39, sans dépendance.
 *
 * Code 39 a été retenu plutôt que Code 128 parce que sa table tient en clair
 * dans ce fichier et se vérifie à l'œil : chaque caractère vaut 9 éléments
 * alternés (barre, espace, barre…) dont exactement 3 sont larges. Toutes les
 * douchettes du commerce le lisent d'origine. La référence est toujours
 * réimprimée en clair sous le code : si une étiquette s'abîme, le vendeur la
 * saisit au pavé.
 */

/** n = élément étroit, w = élément large. Ordre : b s b s b s b s b. */
const TABLE: Record<string, string> = {
  '0': 'nnnwwnwnn',
  '1': 'wnnwnnnnw',
  '2': 'nnwwnnnnw',
  '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw',
  '5': 'wnnwwnnnn',
  '6': 'nnwwwnnnn',
  '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn',
  '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw',
  B: 'nnwnnwnnw',
  C: 'wnwnnwnnn',
  D: 'nnnnwwnnw',
  E: 'wnnnwwnnn',
  F: 'nnwnwwnnn',
  G: 'nnnnnwwnw',
  H: 'wnnnnwwnn',
  I: 'nnwnnwwnn',
  J: 'nnnnwwwnn',
  K: 'wnnnnnnww',
  L: 'nnwnnnnww',
  M: 'wnwnnnnwn',
  N: 'nnnnwnnww',
  O: 'wnnnwnnwn',
  P: 'nnwnwnnwn',
  Q: 'nnnnnnwww',
  R: 'wnnnnnwwn',
  S: 'nnwnnnwwn',
  T: 'nnnnwnwwn',
  U: 'wwnnnnnnw',
  V: 'nwwnnnnnw',
  W: 'wwwnnnnnn',
  X: 'nwnnwnnnw',
  Y: 'wwnnwnnnn',
  Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw',
  '.': 'wwnnnnwnn',
  ' ': 'nwwnnnwnn',
  $: 'nwnwnwnnn',
  '/': 'nwnwnnnwn',
  '+': 'nwnnnwnwn',
  '%': 'nnnwnwnwn',
  '*': 'nwnnwnwnn',
}

/** Caractère de délimitation, obligatoire en tête et en queue. */
const DELIMITEUR = '*'

const LARGEUR_ETROITE = 1
const LARGEUR_LARGE = 3
/** Espace inter-caractères, d'une largeur étroite. */
const SEPARATEUR = 1

export interface Barre {
  x: number
  largeur: number
}

/** Renvoie les barres noires et la largeur totale, en modules. */
export function barresCode39(valeur: string): { barres: Barre[]; largeur: number } {
  const texte = DELIMITEUR + normaliser(valeur) + DELIMITEUR
  const barres: Barre[] = []
  let x = 0

  for (let i = 0; i < texte.length; i += 1) {
    const motif = TABLE[texte[i]!]
    if (!motif) continue
    for (let j = 0; j < motif.length; j += 1) {
      const largeur = motif[j] === 'w' ? LARGEUR_LARGE : LARGEUR_ETROITE
      // Les indices pairs sont des barres, les impairs des espaces.
      if (j % 2 === 0) barres.push({ x, largeur })
      x += largeur
    }
    x += SEPARATEUR
  }

  return { barres, largeur: x - SEPARATEUR }
}

/** Ne garde que ce que Code 39 sait représenter. */
export function normaliser(valeur: string): string {
  return valeur
    .toUpperCase()
    .split('')
    .filter((caractere) => caractere !== DELIMITEUR && caractere in TABLE)
    .join('')
}

/** Le SVG complet, prêt à être posé dans une étiquette. */
export function svgCode39(
  valeur: string,
  options: { hauteur?: number; largeurCible?: number } = {},
): string {
  const hauteur = options.hauteur ?? 40
  const { barres, largeur } = barresCode39(valeur)
  const rectangles = barres
    .map((b) => `<rect x="${b.x}" y="0" width="${b.largeur}" height="${hauteur}" />`)
    .join('')
  const style = options.largeurCible ? ` width="${options.largeurCible}"` : ''
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${largeur} ${hauteur}"` +
    `${style} height="${hauteur}" preserveAspectRatio="none" shape-rendering="crispEdges" fill="#000">` +
    `${rectangles}</svg>`
  )
}

/** Contrôle d'intégrité de la table — utilisé par le script de vérification. */
export function verifierTable(): string[] {
  const anomalies: string[] = []
  const vus = new Map<string, string>()

  for (const [caractere, motif] of Object.entries(TABLE)) {
    if (motif.length !== 9) {
      anomalies.push(`${caractere} : ${motif.length} éléments au lieu de 9`)
      continue
    }
    const larges = motif.split('').filter((e) => e === 'w').length
    if (larges !== 3) anomalies.push(`${caractere} : ${larges} éléments larges au lieu de 3`)

    const barresLarges = [0, 2, 4, 6, 8].filter((i) => motif[i] === 'w').length
    const espacesLarges = [1, 3, 5, 7].filter((i) => motif[i] === 'w').length
    const attendu =
      caractere === '$' || caractere === '/' || caractere === '+' || caractere === '%'
        ? { barres: 0, espaces: 3 }
        : { barres: 2, espaces: 1 }
    if (barresLarges !== attendu.barres || espacesLarges !== attendu.espaces) {
      anomalies.push(
        `${caractere} : ${barresLarges} barres larges / ${espacesLarges} espaces larges — attendu ${attendu.barres}/${attendu.espaces}`,
      )
    }

    const precedent = vus.get(motif)
    if (precedent) anomalies.push(`${caractere} a le même motif que ${precedent}`)
    vus.set(motif, caractere)
  }

  if (Object.keys(TABLE).length !== 44) {
    anomalies.push(`${Object.keys(TABLE).length} caractères dans la table au lieu de 44`)
  }
  return anomalies
}
